import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BookingService } from '../../core/services/booking.service';
import { User } from '../../core/models/user.model';
import { ClinicDoctorProfileDto } from '../../core/models/clinic-doctor-profile.model';
import { DoctorReportSummary } from '../../core/models/doctor-report-summary';
import { NotificationService } from '../../core/services/notification.service';
import { ScheduleCalendarView } from '../schedule-calendar-view/schedule-calendar-view';
import { PersonalScheduleComponent } from '../personal-schedule/personal-schedule';
import { SettingsService } from '../../core/services/settings.service';
import { AppearanceSettings } from '../../core/models/appearance-settings.model';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { TodoService } from '../../core/services/todo.service';
import { TodoModel } from '../../core/models/todo.model';
import { ClinicStatusService } from '../../core/services/clinic-status.service';
import { ClinicStatus } from '../../core/enums/clinic-status.enum';

@Component({
  selector: 'app-reception-dashboard',
  imports: [CommonModule, ScheduleCalendarView, PersonalScheduleComponent, PortalNavigation],
  templateUrl: './reception-dashboard.html',
  styleUrl: './reception-dashboard.scss',
})
export class ReceptionDashboard implements OnInit {
  @ViewChild(ScheduleCalendarView) calendar!: ScheduleCalendarView;

  isLoading = true;
  userProfile: User | null = null;
  clinicDoctors: ClinicDoctorProfileDto[] = [];
  selectedDoctor: ClinicDoctorProfileDto | null | undefined = null;
  doctorId: string | null = null;
  defaultDoctorId: string | null = null;
  apiCalls = 4;
  reportSummary: DoctorReportSummary | undefined;
  appearanceSettings: AppearanceSettings | undefined;
  upcomingTodos: TodoModel[] = [];
  todosLoading = false;
  isClinicActive = false;
  clinicStatus: ClinicStatus | null = null;

  // Pagination properties
  todosCurrentPage = 1;
  todosItemsPerPage = 5;
  todosPageSize: number = 0;

  get primaryNavigationItems(): PortalNavigationItem[] {
    return [
      {
        label: 'Patients',
        icon: 'bi-people-fill',
        route: '/dashboard/patients-view',
        disabled: !this.isClinicActive,
      },
      {
        label: 'Bookings',
        icon: 'bi-calendar2-check-fill',
        route: '/dashboard/booking-list',
        disabled: !this.isClinicActive,
      },
      {
        label: 'Create Booking',
        icon: 'bi-calendar2-plus-fill',
        route: '/dashboard/create-booking',
        disabled: !this.isClinicActive,
      },
    ];
  }

  get clinicStatusDisplay(): { label: string; icon: string; pillClass: string } {
    switch (this.clinicStatus) {
      case ClinicStatus.ACTIVE:
        return {
          label: 'Front Desk Open',
          icon: 'bi-check-circle-fill',
          pillClass: 'status-confirmed',
        };
      case ClinicStatus.REVIEW_PENDING:
        return { label: 'Review Pending', icon: 'bi-clock-history', pillClass: 'status-pending' };
      case ClinicStatus.SUSPENDED:
        return { label: 'Practice Suspended', icon: 'bi-x-octagon', pillClass: 'status-cancelled' };
      case ClinicStatus.INACTIVE:
        return {
          label: 'Practice Inactive',
          icon: 'bi-dash-circle',
          pillClass: 'status-cancelled',
        };
      default:
        return { label: 'Review Pending', icon: 'bi-clock-history', pillClass: 'status-pending' };
    }
  }

  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private settingsService: SettingsService,
    private todoService: TodoService,
    private clinicStatusService: ClinicStatusService,
    private bookingService: BookingService,
  ) {}

  ngOnInit(): void {
    const profile = localStorage.getItem('userProfile');
    if (profile) {
      this.userProfile = JSON.parse(profile);
    }
    const doctors = localStorage.getItem('clinicDoctors');
    if (doctors) {
      this.clinicDoctors = JSON.parse(doctors);
      this.selectedDoctor = this.clinicDoctors[0] ?? null;
      this.doctorId = this.selectedDoctor?.doctorId ?? null;
      this.getReport(this.doctorId);
    }

    this.isLoading = false;
    this.loadAppearanceSettings();
    this.getUpcomingTodos();
    this.clinicStatusService.load();
    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.clinicStatusService.status$.subscribe((status) => {
      this.clinicStatus = status;
    });
  }

  private loadAppearanceSettings(): void {
    this.settingsService.getAppearanceSettings().subscribe(
      (settings) => {
        this.appearanceSettings = settings;

        if (this.appearanceSettings.defaultDoctorId) {
          this.defaultDoctorId = this.appearanceSettings.defaultDoctorId;
          this.selectedDoctor = this.clinicDoctors.find(
            (doctor) => doctor.doctorId === this.defaultDoctorId,
          );
          this.doctorId = this.selectedDoctor?.doctorId ?? null;
        }

        this.isLoading = false;
      },
      (error) => {
        this.notificationService.error('Failed to load appearance settings: ' + error.message);
        this.isLoading = false;
      },
    );
  }

  selectDoctor(doctor: ClinicDoctorProfileDto): void {
    this.selectedDoctor = doctor;
    this.doctorId = doctor.doctorId;
    this.getReport(doctor.doctorId);
  }

  navigateToViewPatients(): void {
    this.router.navigate(['dashboard/patients-view']);
  }

  navigateToViewBookings(): void {
    this.router.navigate(['dashboard/booking-list']);
  }

  navigateToCreateBooking(): void {
    this.router.navigate(['dashboard/create-booking']);
  }

  getReport(doctorId?: string | null): void {
    this.bookingService.getReportSummary(doctorId).subscribe(
      (res) => {
        this.reportSummary = res;
      },
      (error) => {
        this.notificationService.error(error.error.message);
        this.isLoading = false;
      },
    );
  }

  navigateToProfile(): void {
    this.router.navigate(['/dashboard/profile']);
  }

  navigateToSettings(): void {
    this.router.navigate(['/dashboard/settings']);
  }

  openNotifications(): void {
    // future notification screen
  }

  getUpcomingTodos(): void {
    this.todosLoading = true;
    this.todoService.getUpcomingTodos().subscribe({
      next: (res) => {
        this.upcomingTodos = res;
        this.todosPageSize = Math.ceil(res.length / this.todosItemsPerPage);
        this.todosCurrentPage = 1; // Reset to first page
        this.todosLoading = false;
      },
      error: () => {
        this.todosLoading = false;
        // Non-fatal: silently degrade
      },
    });
  }

  get paginatedTodos(): TodoModel[] {
    const startIndex = (this.todosCurrentPage - 1) * this.todosItemsPerPage;
    const endIndex = startIndex + this.todosItemsPerPage;
    return this.upcomingTodos.slice(startIndex, endIndex);
  }

  todosPreviousPage(): void {
    if (this.todosCurrentPage > 1) {
      this.todosCurrentPage--;
    }
  }

  todosNextPage(): void {
    if (this.todosCurrentPage < this.todosPageSize) {
      this.todosCurrentPage++;
    }
  }

  todosGoToPage(page: number): void {
    if (page >= 1 && page <= this.todosPageSize) {
      this.todosCurrentPage = page;
    }
  }

  onTaskCreated(): void {
    this.getUpcomingTodos();
    // Refresh calendar to show newly created task
    if (this.calendar) {
      this.calendar.getUpcomingTodos();
      this.calendar.getDailyTodos();
    }
  }

  deleteUpcomingTodo(todoId: string): void {
    this.todoService.deleteTodo(todoId).subscribe({
      next: () => {
        this.upcomingTodos = this.upcomingTodos.filter((todo) => todo.id !== todoId);
        this.notificationService.success('Task deleted.');
      },
      error: (error) => {
        this.notificationService.error(error?.error?.message || 'Failed to delete task.');
      },
    });
  }
}
